<template lang="pug">
div
  v-row.sysgrao-row
    v-col(
      v-for="card in producerSummaryCards",
      :key="card.key",
      cols="12",
      md="4"
    )
      v-card.producer-summary-card(
        outlined,
        :class="`producer-summary-card--${card.color}`"
      )
        v-card-text.producer-summary-card__content
          .producer-summary-card__top
            .producer-summary-card__icon(
              :class="`producer-summary-card__icon--${card.color}`"
            )
              v-icon(size="20", color="white") {{ card.icon }}
            .producer-summary-card__main
              .producer-summary-card__main-value {{ formatNumber(card.romaneios) }}
              .producer-summary-card__main-label ROMANEIOS
          .producer-summary-card__title {{ card.title }}
          v-divider.producer-summary-card__divider
          .producer-summary-card__stats
            .producer-summary-card__stat
              .producer-summary-card__stat-value {{ formatNumber(card.sacas) }}
              .producer-summary-card__stat-label SACAS
            .producer-summary-card__stat
              .producer-summary-card__stat-value {{ formatNumber(card.toneladas, 'decimal') }}
              .producer-summary-card__stat-label TONELADAS

  v-row.sysgrao-row
    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Ranking de produtores
          v-btn.panel-card__expand(
            outlined,
            small,
            class="sysgrao-action-button",
            @click="dialogOpen = true"
          )
            v-icon(small) mdi-arrow-expand

        v-card-text.panel-card__content
          apexchart(
            v-if="producerRankingPreviewSeries.length",
            type="bar",
            :options="producerRankingChartOptions",
            :series="producerRankingPreviewSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Entradas vs Saídas (toneladas)

        v-card-text.panel-card__content
          apexchart(
            v-if="producerEntryExitSeries.length",
            type="bar",
            :options="producerEntryExitChartOptions",
            :series="producerEntryExitSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

  v-row.sysgrao-row
    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Produtores multi-cliente

        v-card-text.panel-card__table-content
          v-data-table.sysgrao-producer-table(
            :headers="producerTableHeaders",
            :items="producerMultiClientRows",
            :items-per-page="-1",
            fixed-header,
            hide-default-footer,
            no-data-text="Nenhum dado encontrado para o período selecionado.",
            mobile-breakpoint="0"
          )
            template(v-slot:item.presentesEm="{ item }")
              button.sysgrao-producer-table__link(
                type="button",
                @click="openMultiClientDialog(item)"
              ) {{ item.presentesEmText }}
            template(v-slot:item.romaneios="{ item }")
              span.sysgrao-producer-table__number {{ formatNumber(item.romaneios) }}
            template(v-slot:item.toneladas="{ item }")
              span.sysgrao-producer-table__number {{ formatNumber(item.toneladas, 'decimal') }}

    v-col(cols="12", md="6")
      v-card.panel-card(outlined)
        .panel-card__header
          span.panel-card__title Volume por produto (toneladas)

        v-card-text.panel-card__content
          apexchart(
            v-if="producerVolumeSeries.length",
            type="bar",
            :options="producerVolumeChartOptions",
            :series="producerVolumeSeries",
            height="100%"
          )
          .panel-card__empty(v-else)
            span Nenhum dado encontrado para o período selecionado.

  v-dialog(
    :value="dialogOpen",
    @input="dialogOpen = $event",
    content-class="sysgrao-dialog-content",
    max-width="1200px",
    width="1200px"
  )
    v-card.panel-dialog
      .panel-card__header.panel-card__header--dialog
        span.panel-card__title Ranking de produtores
        v-btn(icon, @click="dialogOpen = false")
          v-icon mdi-close
      v-card-text.panel-card__dialog-content.panel-card__dialog-content--scroll
        apexchart(
          v-if="producerRankingDialogSeries.length",
          type="bar",
          :options="producerRankingDialogOptions",
          :series="producerRankingDialogSeries",
          :height="producerRankingDialogHeight"
        )
        .panel-card__empty(v-else)
          span Nenhum dado encontrado para o período selecionado.

  produtor-bases-dialog(
    :value="multiClientDialogOpen",
    :item="selectedMultiClientRow",
    @input="handleMultiClientDialogInput"
  )
</template>

<script>
import ProdutorBasesDialog from "./ProdutorBasesDialog.vue";

const NUMBER_FORMATTERS = {
  integer: new Intl.NumberFormat("pt-BR"),
  decimal: new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 1, maximumFractionDigits: 1 })
};
const PRODUCT_VOLUME_COLORS = {
  soja: "#d4a100",
  milho: "#1faa45",
  outros: "#748291"
};
const INITIAL_RANKING_LIMIT = 10;
const RANKING_DIALOG_MIN_HEIGHT = 520;
const RANKING_DIALOG_BAR_HEIGHT = 44;

const PRODUCER_TABLE_HEADERS = [
  { text: "Produtor", value: "produtor", sortable: false },
  { text: "Presentes em", value: "presentesEm", align: "center", sortable: false, width: "140px" },
  { text: "Romaneios", value: "romaneios", align: "center", sortable: false, width: "110px" },
  { text: "Toneladas", value: "toneladas", align: "end", sortable: false, width: "130px" }
];

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeProductName(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function getProductVolumeColor(productName) {
  const normalized = normalizeProductName(productName);
  if (normalized.includes("soja")) return PRODUCT_VOLUME_COLORS.soja;
  if (normalized.includes("milho")) return PRODUCT_VOLUME_COLORS.milho;
  return PRODUCT_VOLUME_COLORS.outros;
}

function getSortedProducerRankingItems(produtores) {
  return toArray(produtores)
    .filter(item => item.nome)
    .sort((a, b) => b.toneladas - a.toneladas);
}

function buildProducerRankingState(items) {
  const normalizedItems = toArray(items);
  return {
    categories: normalizedItems.map(item => item.nome),
    series: normalizedItems.length
      ? [
          {
            name: "Toneladas",
            data: normalizedItems.map(item => Number(item.toneladas.toFixed(1)))
          }
        ]
      : []
  };
}

function createProducerSummaryCards(movimentacoes = {}) {
  const entradas = movimentacoes.entradas || {};
  const saidas = movimentacoes.saidas || {};
  return [
    {
      key: "entradas",
      title: "Entradas",
      color: "green",
      icon: "mdi-truck-plus",
      romaneios: toNumber(entradas.romaneios),
      sacas: toNumber(entradas.sacas),
      toneladas: toNumber(entradas.toneladas)
    },
    {
      key: "saidas",
      title: "Saídas",
      color: "orange",
      icon: "mdi-truck-minus",
      romaneios: toNumber(saidas.romaneios),
      sacas: toNumber(saidas.sacas),
      toneladas: toNumber(saidas.toneladas)
    },
    {
      key: "geral",
      title: "Geral",
      color: "blue",
      icon: "mdi-silo",
      romaneios: toNumber(entradas.romaneios) + toNumber(saidas.romaneios),
      sacas: toNumber(entradas.sacas) + toNumber(saidas.sacas),
      toneladas: toNumber(entradas.toneladas) + toNumber(saidas.toneladas)
    }
  ];
}

function buildProducerState(movimentacoes, produtores, produtos) {
  const rankingItems = getSortedProducerRankingItems(produtores);
  const volumeItems = toArray(produtos)
    .filter(item => item.descricao)
    .sort((a, b) => b.toneladas - a.toneladas)
    .slice(0, 4);
  const totalEntrada = toNumber(movimentacoes?.entradas?.toneladas);
  const totalSaida = toNumber(movimentacoes?.saidas?.toneladas);
  const totalRomaneios =
    toNumber(movimentacoes?.entradas?.romaneios) + toNumber(movimentacoes?.saidas?.romaneios);

  return {
    rankingItems,
    entryExitSeries: totalRomaneios
      ? [
          {
            name: "Toneladas",
            data: [
              { x: "Entradas", y: Number(totalEntrada.toFixed(1)) },
              { x: "Saídas", y: Number(totalSaida.toFixed(1)) }
            ]
          }
        ]
      : [],
    volumeSeries: volumeItems.length
      ? [
          {
            name: "Toneladas",
            data: volumeItems.map(item => ({
              x: item.descricao,
              y: Number(item.toneladas.toFixed(1))
            }))
          }
        ]
      : [],
    multiClientRows: toArray(produtores)
      .map(item => ({
        hash: item.hash,
        produtor: item.nome,
        presentesEm: item.bases.length,
        presentesEmText: `${item.bases.length} cliente(s)`,
        romaneios: toNumber(item.romaneios),
        toneladas: toNumber(item.toneladas),
        bases: toArray(item.bases)
      }))
      .sort((a, b) => b.toneladas - a.toneladas)
  };
}

function createProducerRankingChartOptions({
  chartHeight,
  categories,
  formatInteger,
  formatDecimal,
  isMobile
}) {
  return {
    chart: { type: "bar", height: chartHeight, parentHeightOffset: 0, toolbar: { show: false } },
    colors: ["#3d4d63"],
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 4,
        barHeight: "68%",
        colors: {
          backgroundBarColors: ["#d7dee8"],
          backgroundBarOpacity: 1,
          backgroundBarRadius: 4
        }
      }
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: "#dfe7f5",
      strokeDashArray: 4,
      padding: isMobile ? { left: 0, right: 0 } : {}
    },
    legend: { show: false },
    xaxis: {
      categories,
      axisBorder: { show: true },
      axisTicks: { show: true },
      labels: {
        show: !isMobile,
        style: { colors: "#5f6b7a", fontSize: "12px" },
        formatter: value => formatInteger(value)
      }
    },
    yaxis: {
      crosshairs: { show: true },
      tooltip: { enabled: false },
      labels: {
        show: !isMobile,
        maxWidth: 220,
        style: { colors: "#3d4b63", fontSize: "12px" }
      }
    },
    tooltip: {
      enabled: true,
      intersect: false,
      followCursor: false,
      y: {
        formatter: value => `${formatDecimal(value)} (TON)`
      }
    }
  };
}

function createProducerVerticalChartOptions({ chartHeight, colors, formatInteger, formatDecimal }) {
  return {
    chart: { type: "bar", height: chartHeight, parentHeightOffset: 0, toolbar: { show: false } },
    colors,
    plotOptions: { bar: { distributed: true, borderRadius: 5, columnWidth: "58%" } },
    dataLabels: { enabled: false },
    grid: { borderColor: "#dfe7f5", strokeDashArray: 4 },
    legend: { show: false },
    xaxis: {
      axisBorder: { show: true },
      axisTicks: { show: true },
      crosshairs: { show: true },
      tooltip: { enabled: true },
      labels: { style: { colors: "#5f6b7a", fontSize: "12px" } }
    },
    yaxis: {
      labels: {
        formatter: value => formatInteger(value),
        style: { colors: "#5f6b7a", fontSize: "12px" }
      }
    },
    tooltip: {
      enabled: true,
      intersect: false,
      followCursor: false,
      y: {
        formatter: value => `${formatDecimal(value)} (TON)`
      }
    }
  };
}

export default {
  name: "ProdutoresTab",

  components: {
    ProdutorBasesDialog
  },

  props: {
    movimentacoes: { type: Object, default: () => ({ entradas: {}, saidas: {} }) },
    produtores: { type: Array, default: () => [] },
    produtos: { type: Array, default: () => [] }
  },

  data() {
    return {
      dialogOpen: false,
      multiClientDialogOpen: false,
      selectedMultiClientRow: null
    };
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown;
    },

    producerSummaryCards() {
      return createProducerSummaryCards(this.movimentacoes);
    },

    producerState() {
      return buildProducerState(this.movimentacoes, this.produtores, this.produtos);
    },

    producerRankingItems() {
      return this.producerState.rankingItems;
    },

    producerRankingPreview() {
      return buildProducerRankingState(this.producerRankingItems.slice(0, INITIAL_RANKING_LIMIT));
    },

    producerRankingDialog() {
      return buildProducerRankingState(this.producerRankingItems);
    },

    producerRankingCategories() {
      return this.producerRankingPreview.categories;
    },

    producerRankingPreviewSeries() {
      return this.producerRankingPreview.series;
    },

    producerRankingDialogCategories() {
      return this.producerRankingDialog.categories;
    },

    producerRankingDialogSeries() {
      return this.producerRankingDialog.series;
    },

    producerRankingDialogHeight() {
      return Math.max(
        RANKING_DIALOG_MIN_HEIGHT,
        this.producerRankingDialogCategories.length * RANKING_DIALOG_BAR_HEIGHT
      );
    },

    producerEntryExitSeries() {
      return this.producerState.entryExitSeries;
    },
    producerVolumeSeries() {
      return this.producerState.volumeSeries;
    },
    producerMultiClientRows() {
      return this.producerState.multiClientRows;
    },
    producerTableHeaders() {
      return PRODUCER_TABLE_HEADERS;
    },

    producerRankingChartOptions() {
      return createProducerRankingChartOptions({
        chartHeight: "100%",
        categories: this.producerRankingCategories,
        formatInteger: v => this.formatNumber(v),
        formatDecimal: v => this.formatNumber(v, "decimal"),
        isMobile: this.isMobile
      });
    },

    producerRankingDialogOptions() {
      return createProducerRankingChartOptions({
        chartHeight: this.producerRankingDialogHeight,
        categories: this.producerRankingDialogCategories,
        formatInteger: v => this.formatNumber(v),
        formatDecimal: v => this.formatNumber(v, "decimal"),
        isMobile: this.isMobile
      });
    },

    producerEntryExitChartOptions() {
      return createProducerVerticalChartOptions({
        chartHeight: "100%",
        colors: ["#1faa45", "#e52525"],
        formatInteger: v => this.formatNumber(v),
        formatDecimal: v => this.formatNumber(v, "decimal")
      });
    },

    producerVolumeChartOptions() {
      const volumeData = this.producerVolumeSeries[0]?.data || [];
      return createProducerVerticalChartOptions({
        chartHeight: "100%",
        colors: volumeData.map(item => getProductVolumeColor(item.x)),
        formatInteger: v => this.formatNumber(v),
        formatDecimal: v => this.formatNumber(v, "decimal")
      });
    }
  },

  methods: {
    formatNumber(value, format = "integer") {
      const n = toNumber(value);
      if (format === "integer") return NUMBER_FORMATTERS.integer.format(Math.round(n));
      return (NUMBER_FORMATTERS[format] || NUMBER_FORMATTERS.integer).format(n);
    },

    openMultiClientDialog(item) {
      this.selectedMultiClientRow = item;
      this.multiClientDialogOpen = true;
    },

    handleMultiClientDialogInput(value) {
      this.multiClientDialogOpen = value;
      if (!value) this.selectedMultiClientRow = null;
    }
  }
};
</script>

<style scoped>
.sysgrao-row {
  margin-left: -6px !important;
  margin-right: -6px !important;
}

.sysgrao-row > [class*="col-"] {
  padding: 6px !important;
}

.producer-summary-card {
  border-color: #d9e2ef !important;
  border-radius: 18px 6px 18px 6px !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  min-height: 178px;
  height: 178px;
  max-height: 178px;
  overflow: hidden;
}

.producer-summary-card--green {
  border-top: 3px solid #0fb46c !important;
}

.producer-summary-card--orange {
  border-top: 3px solid #ff9e0d !important;
}

.producer-summary-card--blue {
  border-top: 3px solid #3d82f6 !important;
}

.producer-summary-card__content {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px 14px !important;
}

.producer-summary-card__top {
  align-items: flex-start;
  display: flex;
  justify-content: space-between;
}

.producer-summary-card__icon {
  align-items: center;
  border-radius: 10px;
  display: inline-flex;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.producer-summary-card__icon--green {
  background-color: #0fb46c;
}
.producer-summary-card__icon--orange {
  background-color: #ff9e0d;
}
.producer-summary-card__icon--blue {
  background-color: #3d82f6;
}

.producer-summary-card__main {
  align-items: flex-end;
  display: flex;
  flex-direction: column;
}

.producer-summary-card__main-value {
  color: #173256;
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
}

.producer-summary-card__main-label {
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 700;
  margin-top: 3px;
}

.producer-summary-card__title {
  color: #26456f;
  font-size: 16px;
  font-weight: 700;
  margin-top: 6px;
}

.producer-summary-card__divider {
  margin: 10px -14px 0;
}

.producer-summary-card__stats {
  display: flex;
  justify-content: space-around;
  margin-top: auto;
  padding-top: 16px;
}

.producer-summary-card__stat {
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  text-align: center;
}

.producer-summary-card__stat-value {
  color: #173256;
  font-size: 18px;
  font-weight: 700;
}

.producer-summary-card__stat-label {
  color: #5f6b7a;
  font-size: 12px;
  font-weight: 700;
  margin-top: 2px;
}

.panel-card {
  border-color: #d9e2ef !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  border-radius: 18px 6px 18px 6px !important;
}

.panel-card {
  display: flex;
  flex-direction: column;
  min-height: 490px;
  height: 490px;
  max-height: 490px;
}

.panel-dialog {
  border-color: #d9e2ef !important;
  border-radius: 10px !important;
  box-shadow: 0 2px 6px rgba(15, 42, 77, 0.08);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  overflow: hidden;
}

.panel-card__header {
  align-items: center;
  display: flex;
  justify-content: space-between;
  padding: 16px 16px 0;
}

.panel-card__header--dialog {
  flex: 0 0 auto;
  padding: 16px;
}

.panel-card__title {
  color: #0a1f44;
  font-size: 16px;
  font-weight: 700;
}

.panel-card__expand {
  height: 30px !important;
  min-width: 36px !important;
  padding: 0 8px !important;
}

.panel-card__content {
  flex: 1 1 0;
  min-height: 0;
  padding-bottom: 16px !important;
}

.panel-card__table-content {
  flex: 1 1 0;
  min-height: 0;
  padding: 0 0 14px !important;
  display: flex;
  flex-direction: column;
}

.panel-card__dialog-content {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.panel-card__dialog-content--scroll {
  flex: 1 1 auto;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
}

::v-deep .sysgrao-dialog-content .v-dialog {
  margin: 24px auto;
  height: calc(100vh - 48px);
  max-height: calc(100vh - 48px);
}

.panel-card__empty {
  align-items: center;
  color: #5f6b7a;
  display: flex;
  height: 100%;
  justify-content: center;
  min-height: 320px;
  text-align: center;
}

.sysgrao-action-button {
  border-color: #c7d1dc !important;
  box-shadow: none !important;
  color: #6f7d8c !important;
  font-size: 11px !important;
  font-weight: 500;
  letter-spacing: 0.01em !important;
  text-transform: none !important;
}

.sysgrao-action-button::before {
  background-color: transparent !important;
}

.sysgrao-action-button .v-icon {
  color: #748291 !important;
}

.sysgrao-producer-table {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  width: 100%;
}

.sysgrao-producer-table ::v-deep .v-data-table {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
}

.sysgrao-producer-table ::v-deep .v-data-table__wrapper {
  flex: 1 1 0;
  min-height: 0;
  max-height: none;
  overflow-y: auto;
  scrollbar-width: thin;
}

.sysgrao-producer-table ::v-deep table thead {
  background-color: #f7f9fc;
}

.sysgrao-producer-table ::v-deep table thead th {
  color: #6b7889 !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.sysgrao-producer-table ::v-deep table thead th span {
  font-size: 14px !important;
  font-weight: 600 !important;
  line-height: 1.2 !important;
  letter-spacing: 0.01em;
}

.sysgrao-producer-table ::v-deep tbody td {
  color: #253f66;
  font-size: 13px;
}

.sysgrao-producer-table__link {
  background: transparent;
  border: 0;
  color: #2b7cd3;
  cursor: pointer;
  font: inherit;
  font-weight: 600;
  padding: 0;
  text-decoration: underline;
}

.sysgrao-producer-table__number {
  color: #173256;
  font-weight: 700;
}

@media (max-width: 959px) {
  .panel-card__header {
    align-items: flex-start;
    flex-direction: column;
    gap: 12px;
  }

  .panel-card__expand {
    align-self: flex-end;
  }

  .producer-summary-card__stats {
    gap: 12px;
  }
}
</style>
